/**
 * "Near me" (plan 7): asks the browser for the user's location only when
 * they tap the button. The location is kept in memory only: never saved,
 * never sent anywhere, never put in the URL.
 */
import { useCallback, useState } from 'react';
import { LatLng } from '../utils/geo';

export type NearMeStatus = 'idle' | 'locating' | 'active' | 'denied' | 'unavailable' | 'insecure';

export function useNearMe() {
  const [status, setStatus] = useState<NearMeStatus>('idle');
  const [location, setLocation] = useState<LatLng | null>(null);

  const request = useCallback(() => {
    // Browsers only share location with https pages (and localhost).
    if (!window.isSecureContext) {
      setStatus('insecure');
      return;
    }
    if (!('geolocation' in navigator)) {
      setStatus('unavailable');
      return;
    }
    setStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setStatus('active');
      },
      (err) => {
        setLocation(null);
        setStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  const clear = useCallback(() => {
    setLocation(null);
    setStatus('idle');
  }, []);

  return { status, location, request, clear };
}
